import React from 'react';
import {
  Card,
  CardContent,
  Typography,
  Stack,
  Chip,
  Avatar,
  CardActionArea,
  Box,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import BusinessIcon from '@mui/icons-material/Business';
import getRoleLabel from '../helpers/getRoleLabel';

interface OrganisationCardProps {
  organisation: {
    id: string;
    name: string;
    legalName?: string;
    role: string;
    joinedAt?: string;
  };
  isSelected: boolean;
  onSelect: (orgId: string) => void;
}

const OrganisationCard: React.FC<OrganisationCardProps> = ({
  organisation,
  isSelected,
  onSelect,
}) => {
  const roleColor =
    organisation.role === 'OWNER'
      ? 'primary'
      : organisation.role === 'ADMIN'
        ? 'secondary'
        : 'default';

  return (
    <Card
      elevation={isSelected ? 8 : 1}
      sx={{
        position: 'relative',
        border: isSelected ? 2 : 1,
        borderColor: isSelected ? 'primary.main' : 'divider',
        transition: 'all 0.3s ease',
        bgcolor: 'white',
      }}
    >
      <CardActionArea onClick={() => onSelect(organisation.id)}>
        <CardContent sx={{ p: 3 }}>
          <Stack direction="row" spacing={2} alignItems="center">
            {/* Organisation Icon */}
            <Avatar
              sx={{
                bgcolor: isSelected ? 'primary.main' : 'action.selected',
                width: 48,
                height: 48,
              }}
            >
              <BusinessIcon />
            </Avatar>

            {/* Organisation Details */}
            <Box flex={1}>
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="h6">{organisation.name}</Typography>
                {isSelected && (
                  <Chip label="Current" size="small" color="primary" sx={{ height: 20 }} />
                )}
              </Stack>
              <Stack direction="row" spacing={1} alignItems="center" mt={0.5}>
                <Chip
                  label={getRoleLabel(organisation.role)}
                  size="small"
                  color={roleColor}
                  sx={{ height: 24 }}
                />
                {organisation.joinedAt && (
                  <Typography variant="caption" color="text.secondary">
                    Joined {new Date(organisation.joinedAt).toLocaleDateString()}
                  </Typography>
                )}
              </Stack>
            </Box>

            {/* Selected Indicator */}
            {isSelected && <CheckCircleIcon color="primary" sx={{ fontSize: 32 }} />}
          </Stack>

          {/* Additional Details */}
          {organisation.legalName && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
              {organisation.legalName}
            </Typography>
          )}
        </CardContent>
      </CardActionArea>
    </Card>
  );
};

export default OrganisationCard;
